import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Edit3, Save, X, Plus } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Reusable component for rendering editable points list
const EditablePointsList = ({ language, points, onPointChange, onAddPoint, onDeletePoint }) => (
  <div style={{ marginBottom: '8px' }}>
    {points.map((point, pointIndex) => (
      <div key={`${language}-${pointIndex}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ color: '#9CA3AF', marginRight: '8px' }}>•</span>
        <input
          type="text"
          value={point}
          onChange={(e) => onPointChange(language, pointIndex, e.target.value)}
          style={{
            flex: 1,
            padding: '6px 12px',
            fontSize: '14px',
            border: '1px solid #D1D5DB',
            borderRadius: '4px',
            outline: 'none',
            transition: 'all 0.2s',
          }}
          placeholder={`Point ${pointIndex + 1}`}
          onFocus={(e) => {
            e.target.style.border = '1px solid #3B82F6';
            e.target.style.boxShadow = '0 0 0 1px #3B82F6';
          }}
          onBlur={(e) => {
            e.target.style.border = '1px solid #D1D5DB';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          onClick={() => onDeletePoint(language, pointIndex)}
          style={{
            padding: '6px',
            color: '#EF4444',
            background: 'transparent',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#FEF2F2')}
          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Trash2 size={14} />
        </button>
      </div>
    ))}
    <button
      onClick={() => onAddPoint(language)}
      style={{
        fontSize: '14px',
        color: '#2563EB',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontWeight: '500',
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = '#1D4ED8')}
      onMouseOut={(e) => (e.currentTarget.style.color = '#2563EB')}
    >
      + Add Point
    </button>
  </div>
);

const AdvisoryRA = () => {
  const [advisories, setAdvisories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAdvisoryId, setEditingAdvisoryId] = useState(null);
  const [editableEnglishHeading, setEditableEnglishHeading] = useState('');
  const [editableEnglishPoints, setEditableEnglishPoints] = useState([]);
  const [editableTetumHeading, setEditableTetumHeading] = useState('');
  const [editableTetumPoints, setEditableTetumPoints] = useState([]);

  const fetchAdvisories = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${backendUrl}/roadaccident/roadaccident-advisories/`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. Response: ${errorText.substring(0, 200)}`);
      }
      const data = await response.json();
      setAdvisories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch advisories:', e);
      setError('Failed to load advisories. Please try again later.');
      setAdvisories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvisories();
  }, [fetchAdvisories]);

  const parseDescriptionToList = (htmlString) => {
    if (!htmlString) return [];
    const matches = [...htmlString.matchAll(/<li>(.*?)<\/li>/g)];
    return matches.map((match) => match[1]);
  };

  const listToDescriptionHtml = (itemsArray) => {
    if (!itemsArray || itemsArray.length === 0) return '<ul></ul>';
    return `<ul>${itemsArray.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  };

  const handleEdit = (advisory) => {
    setEditingAdvisoryId(advisory.id);
    setEditableEnglishHeading(advisory.englishheading || '');
    setEditableEnglishPoints(parseDescriptionToList(advisory.englishdescription || ''));
    setEditableTetumHeading(advisory.tetumheading || '');
    setEditableTetumPoints(parseDescriptionToList(advisory.tetumdescription || ''));
  };

  const handleCancelEdit = () => {
    setEditingAdvisoryId(null);
  };

  const handleSaveEdit = async () => {
    if (editingAdvisoryId === null) return;

    const updatedEnglishDescriptionHtml = listToDescriptionHtml(editableEnglishPoints);
    const updatedTetumDescriptionHtml = listToDescriptionHtml(editableTetumPoints);

    const payload = {
      englishheading: editableEnglishHeading,
      englishdescription: updatedEnglishDescriptionHtml,
      tetumheading: editableTetumHeading,
      tetumdescription: updatedTetumDescriptionHtml,
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/roadaccident/roadaccident-advisories/${editingAdvisoryId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to save advisory (ID: ${editingAdvisoryId}): ${response.status} - ${errorDataText}`);
      }
      handleCancelEdit();
      fetchAdvisories();
    } catch (e) {
      console.error('Error saving advisory:', e);
      setError(`Failed to save changes for advisory ID ${editingAdvisoryId}: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handlePointChange = (language, index, value) => {
    if (language === 'english') {
      const newPoints = [...editableEnglishPoints];
      newPoints[index] = value;
      setEditableEnglishPoints(newPoints);
    } else if (language === 'tetum') {
      const newPoints = [...editableTetumPoints];
      newPoints[index] = value;
      setEditableTetumPoints(newPoints);
    }
  };

  const handleAddPointToList = (language) => {
    if (language === 'english') {
      setEditableEnglishPoints([...editableEnglishPoints, '']);
    } else if (language === 'tetum') {
      setEditableTetumPoints([...editableTetumPoints, '']);
    }
  };

  const handleDeletePoint = (language, index) => {
    if (language === 'english') {
      setEditableEnglishPoints(editableEnglishPoints.filter((_, i) => i !== index));
    } else if (language === 'tetum') {
      setEditableTetumPoints(editableTetumPoints.filter((_, i) => i !== index));
    }
  };

  const handleAddNewAdvisory = async () => {
    const newAdvisoryData = {
      englishheading: 'Write Type',
      englishdescription: '<ul><li>point 1 </li></ul>',
      tetumheading: 'Tipu Hakerek',
      tetumdescription: '<ul><li> foun 1</li></ul>',
    };

    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/roadaccident/roadaccident-advisories/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newAdvisoryData),
      });
      if (!response.ok) {
        let errorDataText = 'Error details not available in JSON format.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to add new advisory: ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error adding new advisory:', e);
      setError(`Failed to add new advisory: ${e.message}`);
      setIsLoading(false);
    }
  };

  const handleDeleteAdvisory = async (advisoryId) => {
    if (!window.confirm('Are you sure you want to delete this advisory section? This action cannot be undone.')) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/roadaccident/roadaccident-advisories/${advisoryId}/`, {
        method: 'DELETE',
        headers: {},
      });
      if (!response.ok && response.status !== 204) {
        let errorDataText = 'Error details not available.';
        try {
          const errorData = await response.json();
          errorDataText = JSON.stringify(errorData);
        } catch (jsonError) {
          errorDataText = await response.text();
        }
        throw new Error(`Failed to delete advisory (ID: ${advisoryId}): ${response.status} - ${errorDataText}`);
      }
      fetchAdvisories();
    } catch (e) {
      console.error('Error deleting advisory:', e);
      setError(`Failed to delete advisory (ID: ${advisoryId}): ${e.message}`);
      setIsLoading(false);
    }
  };

  if (isLoading && advisories.length === 0 && !error) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#374151', fontSize: '16px' }}>Loading advisories...</div>;
  }

  if (error && advisories.length === 0) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#EF4444', fontSize: '16px' }}>{error}</div>;
  }

  return (
    <div style={{ width: '100%', margin: '0 auto', padding: '20px 10px', backgroundColor:'#ffffff', borderRadius:'8px' }}>
      <div style={{ display:'flex', flexDirection:'column',alignItems:'center',justifyContent:'center',marginBottom: '12px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px', color: 'rgba(27, 108, 194, 0.83)' }}>
          Road Safety Advisory Management
        </h1>
        <p style={{ color: '#4B5563', fontSize: '16px' }}>
          Manage road safety advisories and safety recommendations
        </p>
      </div>

      {error && (
        <p
          style={{
            padding: '12px',
            textAlign: 'center',
            color: '#EF4444',
            background: '#FEF2F2',
            border: '1px solid #EF4444',
            borderRadius: '4px',
            marginBottom: '24px',
          }}
        >
          {error}
        </p>
      )}


      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '8px', overflow: 'hidden'}}>
          <thead style={{ backgroundColor: '#dededeff', borderCollapse: 'collapse',borderBottom: '1px solid #E5E7EB'  }}>
            <tr>
              <th
                style={{
                  padding: '12px 24px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  width: '16.67%',
                }}
              >
                Type
              </th>
              <th
                style={{
                  padding: '12px 24px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  width: '16.67%',
                }}
              >
                Tipu
              </th>
              <th
                style={{
                  padding: '12px 24px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  width: '33.33%',
                }}
              >
                Advisory
              </th>
              <th
                style={{
                  padding: '12px 24px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  width: '33.33%',
                }}
              >
                Avizu
              </th>
              <th
                style={{
                  padding: '12px 24px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#111827',
                  width: '96px',
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody style={{ borderTop: 'none', backgroundColor:'#fdfdfdff' }}>
            {advisories.map((advisory) => (
              <tr
                key={advisory.id}
                style={{
                  transition: 'background 0.2s',borderBottom: '1px solid #E5E7EB' 
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#FFFFFF')}
              >
                {editingAdvisoryId === advisory.id ? (
                  <>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <input
                        type="text"
                        value={editableEnglishHeading}
                        onChange={(e) => setEditableEnglishHeading(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          outline: 'none',
                          transition: 'all 0.2s',
                        }}
                        placeholder="English heading"
                        onFocus={(e) => {
                          e.target.style.border = '1px solid #3B82F6';
                          e.target.style.boxShadow = '0 0 0 1px #3B82F6';
                        }}
                        onBlur={(e) => {
                          e.target.style.border = '1px solid #D1D5DB';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <input
                        type="text"
                        value={editableTetumHeading}
                        onChange={(e) => setEditableTetumHeading(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          outline: 'none',
                          transition: 'all 0.2s',
                        }}
                        placeholder="Tetum heading"
                        onFocus={(e) => {
                          e.target.style.border = '1px solid #3B82F6';
                          e.target.style.boxShadow = '0 0 0 1px #3B82F6';
                        }}
                        onBlur={(e) => {
                          e.target.style.border = '1px solid #D1D5DB';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <EditablePointsList
                        language="english"
                        points={editableEnglishPoints}
                        onPointChange={handlePointChange}
                        onAddPoint={handleAddPointToList}
                        onDeletePoint={handleDeletePoint}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <EditablePointsList
                        language="tetum"
                        points={editableTetumPoints}
                        onPointChange={handlePointChange}
                        onAddPoint={handleAddPointToList}
                        onDeletePoint={handleDeletePoint}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={handleSaveEdit}
                          disabled={isLoading}
                          style={{
                            padding: '8px',
                            color: '#16A34A',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                          }}
                          title="Save"
                          onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#F0FDF4')}
                          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isLoading}
                          style={{
                            padding: '8px',
                            color: '#4B5563',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                          }}
                          title="Cancel"
                          onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#F3F4F6')}
                          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <p style={{ fontSize: '14px', color: '#111827' }}>{advisory.englishheading}</p>
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <p style={{ fontSize: '14px', color: '#111827' }}>{advisory.tetumheading}</p>
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <div
                        style={{ fontSize: '14px', color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: advisory.englishdescription || '<ul></ul>' }}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top' }}>
                      <div
                        style={{ fontSize: '14px', color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: advisory.tetumdescription || '<ul></ul>' }}
                      />
                    </td>
                    <td style={{ padding: '16px 24px', verticalAlign: 'top', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEdit(advisory)}
                          disabled={isLoading || editingAdvisoryId !== null}
                          style={{
                            padding: '8px',
                            color: editingAdvisoryId !== null ? '#9CA3AF' : '#2563EB',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading || editingAdvisoryId !== null ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                          }}
                          title="Edit"
                          onMouseOver={(e) =>
                            !(isLoading || editingAdvisoryId !== null) && (e.currentTarget.style.background = '#EFF6FF')
                          }
                          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteAdvisory(advisory.id)}
                          disabled={isLoading || editingAdvisoryId !== null}
                          style={{
                            padding: '8px',
                            color: editingAdvisoryId !== null ? '#9CA3AF' : '#EF4444',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading || editingAdvisoryId !== null ? 'not-allowed' : 'pointer',
                            transition: 'background 0.2s',
                          }}
                          title="Delete"
                          onMouseOver={(e) =>
                            !(isLoading || editingAdvisoryId !== null) && (e.currentTarget.style.background = '#FEF2F2')
                          }
                          onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleAddNewAdvisory}
          disabled={isLoading || editingAdvisoryId !== null}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: isLoading || editingAdvisoryId !== null ? '#9CA3AF' : '#2563EB',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading || editingAdvisoryId !== null ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) =>
            !(isLoading || editingAdvisoryId !== null) && (e.currentTarget.style.background = '#1D4ED8')
          }
          onMouseOut={(e) =>
            !(isLoading || editingAdvisoryId !== null) && (e.currentTarget.style.background = '#2563EB')
          }
        >
          <Plus size={16} />
          Add New Section
        </button>
      </div>

      {advisories.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '16px' }}>
            No road accident advisories available.
          </p>
          <button
            onClick={handleAddNewAdvisory}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#2563EB',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              margin: '0 auto',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#1D4ED8')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#2563EB')}
          >
            <Plus size={16} />
            Add New Section
          </button>
        </div>
      )}

      {isLoading && advisories.length > 0 && (
        <div style={{ textAlign: 'center', padding: '12px', color: '#2563EB', fontWeight: '500', fontSize: '16px' }}>
          Processing...
        </div>
      )}
    </div>
  );
};

export default AdvisoryRA;